import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import type { Job } from '../../types/job';
import type { Course } from '../../types/course';

export function useSavedItems() {
  const [savedJobs, setSavedJobs] = useState<Job[]>([]);
  const [savedCourses, setSavedCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    loadSavedItems();
  }, [user]);

  const loadSavedItems = async () => {
    try {
      // Load saved jobs with job details
      const { data: savedJobsData, error: jobError } = await supabase
        .from('saved_jobs')
        .select(`
          id,
          job_id,
          status,
          notes,
          saved_at,
          jobs!inner (*)
        `)
        .eq('user_id', user?.id);

      if (jobError) throw jobError;

      // Load saved courses with course details
      const { data: savedCoursesData, error: courseError } = await supabase
        .from('saved_courses')
        .select(`
          id,
          course_id,
          notes,
          saved_at,
          courses!inner (*)
        `)
        .eq('user_id', user?.id);

      if (courseError) throw courseError;

      // Extract job and course data
      const jobs = savedJobsData?.map(item => ({
        ...item.jobs,
        savedId: item.id,
        savedAt: item.saved_at,
        status: item.status,
        notes: item.notes
      })) || [];

      const courses = savedCoursesData?.map(item => ({
        ...item.courses,
        savedId: item.id,
        savedAt: item.saved_at,
        notes: item.notes
      })) || [];

      setSavedJobs(jobs);
      setSavedCourses(courses);
    } catch (error) {
      console.error('Error loading saved items:', error);
      toast.error('Failed to load saved items');
    } finally {
      setIsLoading(false);
    }
  };

  const saveJob = async (job: Job) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('saved_jobs')
        .insert({
          user_id: user.id,
          job_id: job.id,
          status: 'saved'
        });

      if (error) throw error;
      
      setSavedJobs(prev => [...prev, {
        ...job,
        savedId: crypto.randomUUID(),
        savedAt: new Date().toISOString(),
        status: 'saved'
      }]);
      
      toast.success('Job saved successfully');
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job');
    }
  };

  const saveCourse = async (course: Course) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('saved_courses')
        .insert({
          user_id: user.id,
          course_id: course.id
        });

      if (error) throw error;
      
      setSavedCourses(prev => [...prev, {
        ...course,
        savedId: crypto.randomUUID(),
        savedAt: new Date().toISOString()
      }]);
      
      toast.success('Course saved successfully');
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save course');
    }
  };

  const removeSavedJob = async (jobId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('saved_jobs')
        .delete()
        .eq('user_id', user.id)
        .eq('job_id', jobId);

      if (error) throw error;
      setSavedJobs(prev => prev.filter(job => job.id !== jobId));
      toast.success('Job removed from saved items');
    } catch (error) {
      console.error('Error removing saved job:', error);
      toast.error('Failed to remove job');
    }
  };

  const removeSavedCourse = async (courseId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('saved_courses')
        .delete()
        .eq('user_id', user.id)
        .eq('course_id', courseId);

      if (error) throw error;
      setSavedCourses(prev => prev.filter(course => course.id !== courseId));
      toast.success('Course removed from saved items');
    } catch (error) {
      console.error('Error removing saved course:', error);
      toast.error('Failed to remove course');
    }
  };

  return {
    savedJobs,
    savedCourses,
    isLoading,
    saveJob,
    saveCourse,
    removeSavedJob,
    removeSavedCourse
  };
}